// A crash in one page must not take the whole app with it.
//
// This sits INSIDE the shell, around the routed page, rather than around the
// whole thing — so a page that throws leaves the sidebar working and the person
// can click somewhere else instead of staring at a white screen. Layout keys it
// on the pathname, which means navigating away clears the error rather than
// leaving it stuck on the new page too.
//
// Ported from Zanetto Tools (phase 7). Dayboard had no error boundary at all.
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // No Sentry yet — that is on the pre-launch list in
    // docs/launch-checklist.md. Until then the console is the only record, so
    // it gets the component stack as well as the error.
    console.error('Page crashed:', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="card mx-auto max-w-lg p-8 text-center" role="alert">
        <h1 className="text-lg font-semibold text-gray-900">This page stopped working</h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-gray-600">
          Nothing you did caused this and nothing has been lost. Your other pages still work:
          pick one from the menu, or reload to try this page again.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
            Reload the page
          </button>
        </div>
        {/* The message, for when Nathan is on the phone to someone. Collapsed,
            because a stack trace is not what most people need to see first. */}
        <details className="mt-6 text-left">
          <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700">
            Technical detail
          </summary>
          <pre className="mt-2 overflow-x-auto rounded bg-gray-50 p-3 text-xs text-gray-700">
            {error.message}
          </pre>
        </details>
      </div>
    );
  }
}
